import { useEffect, useRef, useState } from "react";
import { Target, Save, ImagePlus, DollarSign, Trophy, Gift, RefreshCw, CheckCircle2 } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 700_000;

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No pude leer el archivo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Archivo no es una imagen válida"));
      img.onload = () => {
        const MAX = 900;
        let { width: w, height: h } = img;
        if (w > h && w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
        else if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        let q = 0.82;
        let url = c.toDataURL("image/jpeg", q);
        while (url.length > MAX_BYTES && q > 0.4) { q -= 0.1; url = c.toDataURL("image/jpeg", q); }
        if (url.length > MAX_BYTES) return reject(new Error("Imagen muy pesada"));
        resolve(url);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function AtinalePage() {
  return <DashboardShell active="atinale">{() => <Body />}</DashboardShell>;
}

function Body() {
  const [config, setConfig] = useState({ enabled: false, challenge: { image: "", product_name: "", exact_price: 0 } });
  const [attempts, setAttempts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemStaff, setRedeemStaff] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [lastRedeemed, setLastRedeemed] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/merchants/me/atinale"),
        api.get("/merchants/me/atinale/attempts?days=30"),
      ]);
      setConfig(c.data);
      setAttempts(a.data);
    } catch { toast.error("No pude cargar los datos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setConfig((c) => ({ ...c, challenge: { ...c.challenge, [k]: v } }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(f);
      upd("image", dataUrl);
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/merchants/me/atinale", {
        enabled: config.enabled,
        image: config.challenge.image,
        product_name: config.challenge.product_name,
        exact_price: parseFloat(config.challenge.exact_price) || 0,
      });
      toast.success("Guardado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const redeem = async (e) => {
    e.preventDefault();
    setRedeeming(true); setLastRedeemed(null);
    try {
      const { data } = await api.post("/merchants/me/atinale/redeem", {
        prize_code: redeemCode.trim().toUpperCase(),
        staff_name: redeemStaff.trim(),
      });
      setLastRedeemed(data);
      setRedeemCode(""); setRedeemStaff("");
      toast.success("Premio entregado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No pude canjear"); }
    finally { setRedeeming(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;
  const stats = attempts?.stats || { total: 0, wins: 0, pending_prizes: 0, redeemed: 0 };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1"><Target className="w-3 h-3" /> Gamificación</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Atínale al Precio</h1>
        <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
          Refuerza tu marca de forma divertida. Sube una foto de una prenda o servicio con su precio exacto. Los usuarios adivinarán — quien atine exacto se lleva el premio GRATIS y va a tu local a reclamarlo (¡y compra otras cosas!).
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-[#FF007A]/10 to-[#0055FF]/10 border border-[#FF007A]/20 p-5 flex items-center justify-between gap-4">
        <div>
          <div className="font-heading font-extrabold text-base">Juego {config.enabled ? "ACTIVO" : "PAUSADO"}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {config.enabled ? "Los usuarios verán tu reto en la lista de juegos MANNADA / juegos." : "Actívalo para aparecer en el juego."}
          </div>
        </div>
        <Switch checked={config.enabled} onCheckedChange={(v) => setConfig((c) => ({ ...c, enabled: v }))} data-testid="atinale-toggle" />
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white p-5 grid md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs">Foto del producto / servicio</Label>
          <div className="mt-2 rounded-xl border-2 border-dashed border-neutral-300 aspect-video relative overflow-hidden bg-neutral-50">
            {config.challenge.image ? (
              <img src={config.challenge.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-neutral-400 text-sm">Sin foto</div>
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-2 right-2 bg-white/95 hover:bg-white text-neutral-800 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow"
              data-testid="atinale-upload"
            >
              <ImagePlus className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : config.challenge.image ? "Cambiar" : "Subir"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre del producto o servicio</Label>
            <Input
              value={config.challenge.product_name}
              onChange={(e) => upd("product_name", e.target.value)}
              placeholder="Blusa lino oversize · Corte de cabello · Latte grande"
              className="mt-1 h-11 rounded-xl"
              maxLength={120}
              data-testid="atinale-product-name"
            />
          </div>
          <div>
            <Label className="text-xs">Precio exacto (MXN)</Label>
            <div className="flex items-center gap-2 mt-1">
              <DollarSign className="w-4 h-4 text-neutral-400" />
              <Input
                type="number"
                step="0.01"
                min={1}
                value={config.challenge.exact_price || ""}
                onChange={(e) => upd("exact_price", e.target.value)}
                placeholder="450.00"
                className="h-11 rounded-xl"
                data-testid="atinale-exact-price"
              />
            </div>
            <p className="text-[10px] text-neutral-500 mt-1">Aceptamos margen de ±$0.50 MXN para no rechazar por centavos.</p>
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold" data-testid="atinale-save">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar reto"}
          </Button>
        </div>
      </div>

      {/* Canjear premio */}
      <div className="rounded-2xl border-2 border-[#FF007A]/30 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-[#FF007A]" />
          <h2 className="font-heading font-extrabold text-lg">Entregar premio</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-3">Cuando un ganador llegue a reclamar su premio, marca aquí el código para que quede registrado.</p>
        <form onSubmit={redeem} className="grid md:grid-cols-3 gap-3">
          <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="PRIZE-XXXXXX" className="h-11 rounded-xl font-mono uppercase" required data-testid="atinale-prize-code" />
          <Input value={redeemStaff} onChange={(e) => setRedeemStaff(e.target.value)} placeholder="Tu nombre" className="h-11 rounded-xl" required data-testid="atinale-staff-name" />
          <Button type="submit" disabled={redeeming} className="h-11 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold" data-testid="atinale-redeem-submit">
            <Gift className="w-4 h-4" /> {redeeming ? "…" : "Entregar premio"}
          </Button>
        </form>
        {lastRedeemed && (
          <div className="mt-3 rounded-xl bg-green-50 border border-green-400 p-3 text-sm text-green-800">
            <CheckCircle2 className="w-4 h-4 inline mr-1" /> Premio entregado a <strong>{lastRedeemed.winner}</strong> — {lastRedeemed.product_name}
          </div>
        )}
      </div>

      {/* Reporte */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-extrabold text-lg">Intentos (últimos 30 días)</h2>
          <Button onClick={load} variant="outline" className="rounded-full h-9 text-xs"><RefreshCw className="w-3 h-3" /> Actualizar</Button>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <MiniStat label="Total" value={stats.total} />
          <MiniStat label="Ganadores" value={stats.wins} color="text-green-600" />
          <MiniStat label="Pendientes" value={stats.pending_prizes} color="text-amber-600" />
          <MiniStat label="Entregados" value={stats.redeemed} color="text-[#0055FF]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                <th className="py-2 font-bold">Fecha</th>
                <th className="py-2 font-bold">Usuario</th>
                <th className="py-2 font-bold text-right">Su tiro</th>
                <th className="py-2 font-bold text-right">Precio real</th>
                <th className="py-2 font-bold">Resultado</th>
                <th className="py-2 font-bold">Código premio</th>
              </tr>
            </thead>
            <tbody>
              {(attempts?.attempts || []).map((a) => (
                <tr key={a.attempt_id} className="border-b border-neutral-100">
                  <td className="py-2 text-xs">{new Date(a.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="py-2">{a.user_name}</td>
                  <td className="py-2 text-right">${a.guess?.toFixed(2)}</td>
                  <td className="py-2 text-right">${a.exact_price?.toFixed(2)}</td>
                  <td className="py-2">
                    {a.won ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${a.prize_status === "redeemed" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-green-100 text-green-700 border-green-300"}`}>
                        {a.prize_status === "redeemed" ? "ENTREGADO" : "GANADOR"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-neutral-400 uppercase tracking-widest font-bold">Cerca</span>
                    )}
                  </td>
                  <td className="py-2 font-mono text-xs text-[#FF007A]">{a.prize_code || "—"}</td>
                </tr>
              ))}
              {(!attempts?.attempts || attempts.attempts.length === 0) && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-neutral-500">Aún nadie ha jugado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-neutral-50 p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">{label}</div>
      <div className={`font-heading font-extrabold text-2xl mt-1 leading-none ${color || ""}`}>{value}</div>
    </div>
  );
}
